import { useFormik } from 'formik';
import { useMemo } from 'react';
import { resolveValueOrDefault } from '~/modules/common/components/FormCustomField';
import { isoStringToObject } from '~/modules/common/dates/convert';
import {
  TIME_AND_EXPENSE_ENTRY_TYPE,
  TASK_ESTIMATE_CALCULATION_TYPES
} from '~/modules/common/enums';
import { useIsBreakpointDown } from '~/modules/common/hooks';
import buildValidationSchema from './buildValidationSchema';
import useOnSubmit from './useOnSubmit';

const getInitialValuesForTimeAndExpenseEntryType = ({
  project,
  showBillingOptions
}) => {
  return showBillingOptions &&
    project?.billingType?.id !== 'urn:replicon:billing-type:non-billable'
    ? TIME_AND_EXPENSE_ENTRY_TYPE.BILLABLE_AND_NON_BILLABLE
    : TIME_AND_EXPENSE_ENTRY_TYPE.NON_BILLABLE;
};

export const getInitialValues = ({
  baseCurrency,
  featureFlags,
  startDate,
  endDate,
  customFieldDefinitions,
  project,
  isMilestone = false,
  areStartAndEndDatesRequired = false,
  isMobile = false,
  isCreateAllocationEnabled = true,
  showBillingOptions
}) => {
  const { defaultBillingCurrency } = project;
  const {
    PSAPRPTaskEstimateCalculation,
    isPsaRmpTaskAllocation1Enabled
  } = featureFlags;
  const isTaskAssignmentsEnabled =
    PSAPRPTaskEstimateCalculation ===
    TASK_ESTIMATE_CALCULATION_TYPES.TASKASSIGNMENT;

  const copyParentTaskResourceAllocations = !(
    isTaskAssignmentsEnabled && !isMobile
  );

  const initialCostCurrency = defaultBillingCurrency || baseCurrency;

  return {
    name: '',
    code: '',
    startDate: startDate ? isoStringToObject(startDate) : null,
    endDate: endDate ? isoStringToObject(endDate) : null,
    assignedUser: null,
    assignedRole: null,
    description: '',
    isMilestone,
    initialAllocateResourceTimeValue: isCreateAllocationEnabled,
    currentAllocateResourceTimeValue: isCreateAllocationEnabled,
    areStartAndEndDatesRequired,
    estimatedHours: '',
    estimatedCost: {
      amount: null,
      currency: {
        id: initialCostCurrency.id,
        displayText: initialCostCurrency.displayText
      }
    },
    isTimeEntryAllowed: true,
    timesheetAccess: [],
    timeAndExpenseEntryType: {
      id: getInitialValuesForTimeAndExpenseEntryType({
        project,
        showBillingOptions
      })
    },
    ...customFieldDefinitions.reduce((fields, definition) => {
      const customFieldValue = resolveValueOrDefault([], definition);

      return {
        ...fields,
        ...(customFieldValue !== null
          ? { [definition.uri]: customFieldValue }
          : {})
      };
    }, {}),
    assignedUserRoleId: null,
    copyParentTaskResourceAllocations,
    ...(isPsaRmpTaskAllocation1Enabled
      ? { resourceEstimates: [] }
      : { resourceAllocations: [] })
  };
};

const useTaskState = ({
  intl,
  baseCurrency,
  featureFlags,
  startDate,
  endDate,
  createTask,
  setMessage,
  onClose,
  validateTaskNameMutation,
  parentUri,
  customFieldDefinitions,
  project,
  isMilestone,
  isCreateAllocationEnabled,
  areStartAndEndDatesRequired,
  hasViewProjectBillingOptions
}) => {
  const isMobile = useIsBreakpointDown('xs');
  const { isPsaRmpTaskAllocation1Enabled } = featureFlags;

  const showBillingOptions = hasViewProjectBillingOptions;

  const validationSchema = useMemo(
    () =>
      buildValidationSchema({
        intl,
        customFieldDefinitions,
        isPsaRmpTaskAllocation1Enabled
      }),
    [customFieldDefinitions, intl, isPsaRmpTaskAllocation1Enabled]
  );

  const onSubmit = useOnSubmit({
    validateTaskNameMutation,
    createTask,
    setMessage,
    onClose,
    parentUri,
    customFieldDefinitions,
    project,
    isPsaRmpTaskAllocation1Enabled,
    shouldRollupTaskDates: areStartAndEndDatesRequired
  });

  const formik = useFormik({
    validationSchema,
    initialValues: getInitialValues({
      baseCurrency,
      featureFlags,
      startDate,
      endDate,
      customFieldDefinitions,
      project,
      isMilestone,
      areStartAndEndDatesRequired,
      isMobile,
      isCreateAllocationEnabled,
      showBillingOptions
    }),
    onSubmit,
    enableReinitialize: true
  });

  return formik;
};

export default useTaskState;
